/**
 * captureEmail — single source of truth for new email signups.
 *
 * Replaces the 14 places that each independently call
 *   supabase.from("email_signups").insert({...})
 * with subtly different shapes (UTM cols, tags, source string, name field).
 *
 * Every BHG signup flow should go through this helper. It:
 *  1. Validates the email (basic shape — server still enforces uniqueness)
 *  2. Inserts into email_signups (handles 23505 duplicate as soft-success)
 *  3. Auto-attaches UTM columns from the persisted UTM cookies, and the
 *     browser's first touch (lib/firstTouch.ts) as first_* tags
 *  4. Fires Beehiiv sync via the shared syncToBeehiiv helper
 *     (which also sets localStorage.emailSubscribed = true for popup
 *     suppression)
 *  5. Fires the unified trackLead pixel (Meta/GA4/Reddit/Ads)
 *
 * Audit reference: code-health audit 2026-05-13, item #4.
 *
 * Usage:
 *   const result = await captureEmail({
 *     email,
 *     source: "nootropics-quiz",
 *     tags: ["nootropics", "quiz", goal],
 *     trackLead: { value: 35, content_id: "nootropics-quiz", ... },
 *   });
 *   if (result.ok) toast.success("You're in!");
 *   else toast.error(result.error);
 */

import { supabase } from "@/integrations/supabase/client";
import { syncToBeehiiv } from "@/lib/syncToBeehiiv";
import { getUtmColumns } from "@/hooks/useUtmPersistence";
import { firstTouchTags, getFirstTouch } from "@/lib/firstTouch";
import { markSubscribed } from "@/lib/subscriber";
import { trackLead as fireLeadPixel, leadDedupEventId } from "@/lib/analytics";
import type { TrackProps } from "@/lib/analytics";

type LeadEvent = TrackProps;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface CaptureEmailInput {
  email: string;
  /** Where this signup originated. Used in DB + Beehiiv tag. */
  source: string;
  /** Optional Beehiiv tags. Always also synced with `source` as the first tag. */
  tags?: string[];
  /** Optional custom name field (some forms store signup_source here). */
  name?: string;
  /**
   * Optional Meta/GA4/Reddit/Ads lead event payload. If omitted, no lead
   * pixel fires (use this for low-value signups; pass it for lead-magnet
   * tier signups to feed CAPI/optimizer signals).
   */
  trackLead?: Partial<TrackProps>;
  /**
   * Store the lead without writing to Beehiiv. The row then records
   * newsletter_consent: false, so the admin bulk re-sync skips it as well.
   * Before that column existed this flag held only until someone pressed
   * "Sync All to Beehiiv", which subscribed the address anyway.
   */
  skipBeehiiv?: boolean;
  /**
   * What the reader agreed to, where the flow knows. Leave it out and the value comes from
   * skipBeehiiv, which is right for a form whose own copy says it subscribes them: true
   * normally, false for a decline.
   *
   * Pass null for a flow that stores an address without ever mentioning the newsletter: a
   * waitlist, a profile claim, a step that auto-submits for a signed-in reader. The row then
   * records unknown instead of claiming consent. Unknown still syncs, so this changes what we
   * store, not what we send.
   */
  newsletterConsent?: boolean | null;
}

export interface CaptureEmailResult {
  ok: boolean;
  /** True when the row was inserted; false when it already existed (still success-UX). */
  inserted: boolean;
  /** When ok=false, a user-facing error string. */
  error?: string;
}

/**
 * The canonical email-signup path. Every BHG form should call this.
 */
export async function captureEmail(input: CaptureEmailInput): Promise<CaptureEmailResult> {
  const email = (input.email ?? "").trim().toLowerCase();

  if (!email || !EMAIL_REGEX.test(email)) {
    return { ok: false, inserted: false, error: "Please enter a valid email address." };
  }

  const utmCols = getUtmColumns();
  const tags = input.tags ?? [];
  // Where this browser first came from (lib/firstTouch.ts), as first_source:..., first_medium:... tags. The
  // email_signups table has no column for it (checked 2026-10-24: id, email, source, tags, utm_source, utm_medium,
  // utm_campaign, utm_content, utm_term, beehiiv_synced, beehiiv_synced_at, created_at) and sync-to-beehiiv only
  // reads email, source and tags, so tags are the one place both accept. The utm_* columns stay latest-touch.
  const firstTouch = firstTouchTags(getFirstTouch()).filter((t) => !tags.includes(t));
  const signupTags = [...tags, ...firstTouch];

  let inserted = true;
  try {
    // NOTE: do NOT include `name` here. The email_signups table has no
    // `name` column (PGRST204), and callers historically passed
    // "signup_source:<x>" into name. We persist that attribution via
    // `source` + Beehiiv tags instead, so dropping name silently is safe.
    // newsletter_consent travels with the row because the row outlives this call: the admin
    // "Sync All to Beehiiv" button re-reads every row with beehiiv_synced = false and subscribes
    // it. Without consent on the row, a lead stored with skipBeehiiv looked exactly like one
    // whose sync had not run yet, so the button would subscribe it later (migration 20261025150000).
    // An explicitly passed value wins, null included. `in` rather than a truthiness or an
    // undefined check, so a deliberate null is not read as an omitted field. Otherwise
    // skipBeehiiv decides it, since a caller that syncs is a caller whose form said so.
    const consent =
      "newsletterConsent" in input ? input.newsletterConsent ?? null : !input.skipBeehiiv;

    const row: Record<string, unknown> = {
      email,
      source: input.source,
      tags: signupTags,
      newsletter_consent: consent,
      ...utmCols,
    };

    let { error } = await supabase.from("email_signups").insert(row as any);

    // PGRST204 = no such column in the schema cache. The column arrives by migration, applied by
    // hand before this branch merges; if the two ever land out of order, store the lead without
    // consent rather than failing the signup in front of the visitor. Remove once the column is
    // live everywhere.
    if (error && (error as any).code === "PGRST204") {
      console.warn("captureEmail: no newsletter_consent column yet, storing the lead without it");
      const { newsletter_consent: _unused, ...legacyRow } = row;
      ({ error } = await supabase.from("email_signups").insert(legacyRow as any));
    }

    if (error) {
      // 23505 = unique-violation. User already subscribed at this source.
      // Treat as success for UX so they still get the lead-magnet / next step.
      if ((error as any).code === "23505") {
        inserted = false;
      } else {
        console.warn("captureEmail insert failed:", error);
        return {
          ok: false,
          inserted: false,
          error: "Something went wrong. Please try again.",
        };
      }
    }
  } catch (err) {
    console.warn("captureEmail unexpected error:", err);
    return {
      ok: false,
      inserted: false,
      error: "Something went wrong. Please try again.",
    };
  }

  // One write to Beehiiv per signup (email audit 2026-10-24, 3.8), and none when the caller passes skipBeehiiv.
  // sync-to-beehiiv stores the source as a custom field, tags the subscriber and starts the cheat sheet sequence
  // where the tags call for it. Until 2026-10-24 a second, unconditional call to subscribe-lead also subscribed the
  // address with the opposite settings (welcome email on), outside the skipBeehiiv check, so an unticked opt-out
  // box subscribed people anyway. BodyHackGuide -> Beehiiv, BHG Labs (the store) -> Omnisend; do not cross-wire the
  // two. The static landers still post to subscribe-lead directly; that endpoint stays.
  if (!input.skipBeehiiv) {
    syncToBeehiiv(email, input.source, signupTags);
    // Tell every form, gate and popup on this page that this browser has signed up.
    markSubscribed();
  }

  // Lead pixel — only fires if the caller passed a trackLead payload.
  if (input.trackLead) {
    try {
      // Deterministic dedup id (bhg_lead_<sha256(email)>) so THIS browser Lead
      // and the server-side Beehiiv → reddit-capi webhook Lead for the same
      // subscriber collapse into ONE Reddit conversion instead of double-
      // counting. Falls back to a random id in track() if crypto rejects.
      let _eventId: string | undefined;
      try {
        _eventId = await leadDedupEventId(email);
      } catch {
        /* non-secure context — track() mints a random id */
      }
      fireLeadPixel({
        // Pass the (validated, lowercased) email so the Lead event carries
        // advanced-matching identity to Reddit + Meta (pixel) and the CAPI
        // `em` field. This is what raises Reddit's "Low email coverage" match
        // score. analytics.track() extracts `email` + `_eventId` out before
        // they can reach GA4/Plausible, so no PII leaks to those.
        email,
        _eventId,
        source: input.source,
        ...utmCols,
        ...input.trackLead,
      } as TrackProps);
    } catch (err) {
      console.warn("captureEmail trackLead failed:", err);
      // Non-fatal — the email is captured + synced regardless.
    }
  }

  return { ok: true, inserted };
}
